/**
 * EXIF Date extraction utility
 * Provides fallback date resolution for photos with missing taken_at but available exif_data
 * Uses priority: CreateDate > DateTimeOriginal > ModifyDate > regex fallback
 */

import { Photo } from '../types';
import { createLogger } from './logger';

const logger = createLogger('exifDate');

/**
 * Resolve the date taken for a photo
 * Priority 1: photo.taken_at from database (already extracted/parsed at upload)
 * Priority 2: Extract from photo.exif_data raw EXIF tags using priority logic
 * If no valid date found, returns null (and UI won't show a Date Taken field)
 * @param photo - Photo object with taken_at and exif_data
 * @returns Date object or null if no date found
 */
export function resolveDateTaken(photo: Photo | null | undefined): Date | null {
  if (!photo) {
    return null;
  }

  // Priority 1: Use taken_at if already set in database
  if (photo.taken_at) {
    try {
      const date = new Date(photo.taken_at);
      if (!isNaN(date.getTime())) {
        logger.debug(`Using photo.taken_at: ${photo.taken_at}`);
        return date;
      }
    } catch {
      logger.warn(`Failed to parse photo.taken_at: ${photo.taken_at}`);
    }
  }

  // Priority 2: Extract from exif_data raw EXIF tags
  if (photo.exif_data && typeof photo.exif_data === 'object') {
    const tags = photo.exif_data;

    // Priority 2.1: CreateDate
    if (tryParseDate(tags.CreateDate)) {
      logger.debug('Using CreateDate from exif_data');
      return tryParseDate(tags.CreateDate);
    }

    // Priority 2.2: DateTimeOriginal
    if (tryParseDate(tags.DateTimeOriginal)) {
      logger.debug('Using DateTimeOriginal from exif_data');
      return tryParseDate(tags.DateTimeOriginal);
    }

    // Priority 2.3: ModifyDate
    if (tryParseDate(tags.ModifyDate)) {
      logger.debug('Using ModifyDate from exif_data');
      return tryParseDate(tags.ModifyDate);
    }

    // Priority 2.4: Regex fallback - scan all keys matching /date/i
    for (const [key, value] of Object.entries(tags)) {
      if (/date/i.test(key) && value !== null && value !== undefined) {
        const parsed = tryParseDate(value);
        if (parsed) {
          logger.debug(`Using ${key} from exif_data via regex fallback`);
          return parsed;
        }
      }
    }
  }

  logger.debug('No valid date found in photo or exif_data');
  return null;
}

/**
 * Try to parse a value as a Date
 * @param value - Value to parse (could be Date, string, number, or any type)
 * @returns Date object if parseable and valid, null otherwise
 */
function tryParseDate(value: any): Date | null {
  if (value === null || value === undefined) {
    return null;
  }

  try {
    let date: Date;

    if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'string' || typeof value === 'number') {
      date = new Date(value);
    } else {
      return null;
    }

    // Check if date is valid
    if (!isNaN(date.getTime())) {
      return date;
    }
  } catch {
    // Parsing failed
  }

  return null;
}

/**
 * Format a date for display in the UI
 * @param date - Date to format
 * @returns Formatted date string (e.g., "January 28, 2007")
 */
export function formatDateTakenForUI(date: Date): string {
  try {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return 'Invalid date';
  }
}
